/**
 * Macchina a stati del cronometro.
 *
 * Lo stato non e memorizzato da nessuna parte: si deriva interamente dagli
 * eventi di periodo e dalle sospensioni. Il client non incrementa alcun
 * contatore, richiama questa funzione a ogni tick. Se il dispositivo va in
 * sospensione, cambia scheda o ricarica la pagina, il minuto resta corretto
 * perche dipende solo da orari gia salvati.
 */

import type { Evento, Formato, Sospensione } from './formato.js';
import {
  eventiAttivi,
  fineRegolamentare,
  formattaMinuto,
  formattaOrologio,
  offsetPeriodo,
} from './formato.js';

export type StatoCronometro = 'tra_periodi' | 'in_gioco' | 'sospesa' | 'terminata';

export interface Cronometro {
  stato: StatoCronometro;
  /** Periodo in corso, oppure il prossimo da iniziare. */
  periodo: number;
  /** Minuto da attribuire a un evento registrato adesso. */
  minuto: number;
  secondo: number;
  /** Cronometro a schermo, es. "45:32". */
  orologio: string;
  /** Minuto in notazione da tabellino, es. "45+2'". */
  etichetta: string;
  /**
   * Minuti di gioco dal fischio d'inizio, recuperi dei periodi chiusi
   * compresi: e il minuto a cui si contano i minuti giocati.
   */
  minutoEffettivo: number;
  /** Minuti oltre il tempo regolamentare del periodo, 0 se dentro. */
  recupero: number;
  /** Comando disponibile all'utente in questo stato. */
  prossimaAzione: 'inizia_periodo' | 'termina_periodo' | 'riprendi' | null;
}

const ms = (iso: string): number => new Date(iso).getTime();

/** Ordine canonico: periodo, minuto, secondo, poi istante reale. */
export function ordinaEventi(eventi: readonly Evento[]): Evento[] {
  return [...eventi].sort(
    (a, b) =>
      a.periodo - b.periodo ||
      a.minuto - b.minuto ||
      a.secondo - b.secondo ||
      ms(a.orarioReale) - ms(b.orarioReale),
  );
}

/**
 * Millisecondi di sospensione da scontare nel periodo fino all'istante dato.
 * Una sospensione ancora aperta conta fino a quell'istante.
 */
function sospensioniMs(sospensioni: readonly Sospensione[], periodo: number, fino: number): number {
  let totale = 0;
  for (const s of sospensioni) {
    if (s.periodo !== periodo) continue;
    const inizio = ms(s.orarioInizio);
    if (inizio > fino) continue;
    const fine = s.orarioFine ? Math.min(ms(s.orarioFine), fino) : fino;
    totale += Math.max(0, fine - inizio);
  }
  return totale;
}

function sospensioneAperta(
  sospensioni: readonly Sospensione[],
  periodo: number,
): Sospensione | undefined {
  return sospensioni.find((s) => s.periodo === periodo && s.orarioFine === null);
}

/**
 * Converte il tempo trascorso nel periodo in minuto di tabellino.
 *
 * Il minuto e a base 1: i primi 59 secondi di gioco sono il minuto 1, come
 * si legge in qualunque tabellino. Ne consegue che al calcio d'inizio del
 * secondo tempo di una 2x45 l'orologio segna 45:00 e un evento immediato
 * vale 46'.
 */
export function minutoDaTrascorso(
  trascorsoMs: number,
  periodo: number,
  f: Formato,
): { minuto: number; secondo: number; secondiNelPeriodo: number } {
  const secondiNelPeriodo = Math.max(0, Math.floor(trascorsoMs / 1000));
  return {
    minuto: offsetPeriodo(periodo, f) + Math.floor(secondiNelPeriodo / 60) + 1,
    secondo: secondiNelPeriodo % 60,
    secondiNelPeriodo,
  };
}

function componi(
  stato: StatoCronometro,
  periodo: number,
  minuto: number,
  secondo: number,
  secondiNelPeriodo: number,
  f: Formato,
  prossimaAzione: Cronometro['prossimaAzione'],
): Omit<Cronometro, 'minutoEffettivo'> {
  const fine = fineRegolamentare(periodo, f);
  return {
    stato,
    periodo,
    minuto,
    secondo,
    orologio: formattaOrologio(
      offsetPeriodo(periodo, f) + Math.floor(secondiNelPeriodo / 60),
      secondo,
    ),
    etichetta: formattaMinuto(minuto, periodo, f),
    recupero: Math.max(0, minuto - fine),
    prossimaAzione,
  };
}

export function calcolaCronometro(
  f: Formato,
  eventi: readonly Evento[],
  sospensioni: readonly Sospensione[],
  adesso: Date,
): Cronometro {
  const c = cronometroSulTabellone(f, eventi, sospensioni, adesso);
  return { ...c, minutoEffettivo: convertiMinutoEffettivo(f, eventi)(c.periodo, c.minuto) };
}

/**
 * Converte il minuto del cronometro in minuti di gioco effettivi.
 *
 * Il cronometro numera ogni periodo dal suo inizio regolamentare: il secondo
 * tempo riparte dal 45' anche dopo cinque minuti di recupero, come vuole la
 * notazione del calcio. Per contare i minuti giocati serve invece il tempo
 * trascorso, e a ogni periodo gia chiuso si aggiunge il suo scarto dalla fine
 * regolamentare: il recupero se e finito dopo, un numero negativo se e finito
 * prima. Un periodo senza fischio di fine vale la durata regolamentare.
 *
 * Restituisce una funzione perche gli scarti si calcolano una volta sola per
 * tutti gli eventi della partita.
 */
export function convertiMinutoEffettivo(
  f: Formato,
  eventi: readonly Evento[],
): (periodo: number, minuto: number) => number {
  const scartoFinoA = [0];
  for (let p = 1; p < f.nPeriodi; p++) {
    const fine = fineEffettivaPeriodo(eventi, p);
    scartoFinoA[p] =
      scartoFinoA[p - 1]! + (fine === undefined ? 0 : fine - fineRegolamentare(p, f));
  }
  return (periodo, minuto) => minuto + scartoFinoA[Math.min(Math.max(periodo, 1), f.nPeriodi) - 1]!;
}

function cronometroSulTabellone(
  f: Formato,
  eventi: readonly Evento[],
  sospensioni: readonly Sospensione[],
  adesso: Date,
): Omit<Cronometro, 'minutoEffettivo'> {
  const inizi = new Map<number, Evento>();
  const fini = new Map<number, Evento>();
  for (const e of eventi) {
    if (e.tipo === 'inizio_periodo') inizi.set(e.periodo, e);
    else if (e.tipo === 'fine_periodo') fini.set(e.periodo, e);
  }

  const periodoAperto = [...inizi.keys()].filter((p) => !fini.has(p)).sort((a, b) => b - a)[0];

  // --- Periodo in corso: in gioco oppure sospeso -----------------------------
  if (periodoAperto !== undefined) {
    const inizio = ms(inizi.get(periodoAperto)!.orarioReale);
    const aperta = sospensioneAperta(sospensioni, periodoAperto);

    // Durante una sospensione il tempo si congela all'istante in cui e
    // iniziata: l'interruzione non deve consumare minuti di gioco.
    const riferimento = aperta ? ms(aperta.orarioInizio) : adesso.getTime();
    const trascorso = riferimento - inizio - sospensioniMs(sospensioni, periodoAperto, riferimento);

    const { minuto, secondo, secondiNelPeriodo } = minutoDaTrascorso(trascorso, periodoAperto, f);
    return componi(
      aperta ? 'sospesa' : 'in_gioco',
      periodoAperto,
      minuto,
      secondo,
      secondiNelPeriodo,
      f,
      aperta ? 'riprendi' : 'termina_periodo',
    );
  }

  // --- Nessun periodo aperto -------------------------------------------------
  const conclusi = [...fini.keys()].sort((a, b) => b - a);
  const ultimoConcluso = conclusi[0] ?? 0;

  if (ultimoConcluso >= f.nPeriodi) {
    const ultimo = fini.get(ultimoConcluso)!;
    return componi(
      'terminata',
      ultimoConcluso,
      ultimo.minuto,
      0,
      (ultimo.minuto - offsetPeriodo(ultimoConcluso, f)) * 60,
      f,
      null,
    );
  }

  // Prima del fischio d'inizio e negli intervalli il comportamento e lo
  // stesso: cronometro fermo sul minuto di fine del periodo precedente.
  // Cambia solo l'etichetta del pulsante, ricavata dal contatore.
  const prossimo = ultimoConcluso + 1;
  const minutoFermo = offsetPeriodo(prossimo, f);
  return componi('tra_periodi', prossimo, minutoFermo, 0, 0, f, 'inizia_periodo');
}

/**
 * Minuto dell'evento di fine del periodo, recupero compreso, oppure
 * `undefined` se il periodo non e stato concluso.
 */
export function fineEffettivaPeriodo(
  eventi: readonly Evento[],
  periodo: number,
): number | undefined {
  return eventiAttivi(eventi).find((e) => e.tipo === 'fine_periodo' && e.periodo === periodo)
    ?.minuto;
}

/**
 * Minuti ammessi per un evento inserito a mano nel periodo indicato.
 *
 * Il minimo e l'offset e non l'offset + 1: un evento registrato
 * all'intervallo porta il minuto fermo del cronometro, che coincide con
 * l'offset del periodo successivo.
 *
 * Il massimo e la fine regolamentare piu un margine di recupero
 * convenzionale, anche se il periodo e stato cronometrato e chiuso prima: un
 * periodo terminato per errore dopo pochi minuti non deve impedire di
 * registrare quello che e successo dopo. `fineEffettiva`, il minuto
 * dell'evento di fine di quel periodo, alza il limite solo quando il
 * recupero cronometrato supera il margine.
 */
export function intervalloMinutoManuale(
  periodo: number,
  f: Formato,
  fineEffettiva?: number,
): { minimo: number; massimo: number } {
  return {
    minimo: offsetPeriodo(periodo, f),
    massimo: Math.max(fineRegolamentare(periodo, f) + 15, fineEffettiva ?? 0),
  };
}

/**
 * Validazione del minuto inserito a mano, usata quando il cronometro non sta
 * girando (partita conclusa, intervallo, gara mai cronometrata).
 *
 * I limiti del messaggio sono nella stessa notazione che l'utente vede
 * nell'anteprima e nel tabellino: `45+2'`, non 47.
 */
export function validaMinutoManuale(
  minuto: number,
  periodo: number,
  f: Formato,
  fineEffettiva?: number,
): { valido: true } | { valido: false; messaggio: string } {
  if (periodo < 1 || periodo > f.nPeriodi) {
    return { valido: false, messaggio: `Il periodo deve essere tra 1 e ${f.nPeriodi}.` };
  }
  const { minimo, massimo } = intervalloMinutoManuale(periodo, f, fineEffettiva);
  if (minuto < minimo || minuto > massimo) {
    const da = formattaMinuto(minimo, periodo, f);
    const a = formattaMinuto(massimo, periodo, f);
    return { valido: false, messaggio: `Nel ${periodo}° periodo il minuto va da ${da} a ${a}.` };
  }
  return { valido: true };
}
